import { Gauge } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "../../components/IconButton/IconButton";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";

export interface UsageIndicatorBudget {
  /** Pre-formatted, e.g. "<$0.01". The consumer owns currency/threshold formatting. */
  spent: string;
  limit: string;
  percentUsed: number;
  resetsLabel: string;
}

export interface UsageIndicatorStats {
  periodLabel: string;
  cost: string;
  requests: string;
  tokensIn: string;
  tokensOut: string;
}

export interface UsageIndicatorProps {
  budget: UsageIndicatorBudget;
  stats: UsageIndicatorStats;

  onViewAllUsage?: () => void;
  /** @deprecated Use `labels.viewAllUsage`. */
  viewAllUsageLabel?: string;
  /** Overrides for the built-in strings (see DEFAULT_USAGE_INDICATOR_LABELS). */
  labels?: Partial<UsageIndicatorLabels>;
}

/** Every built-in string, so apps can translate them. Templated ones take the (already formatted) value. */
export interface UsageIndicatorLabels {
  /** Accessible name of the sidebar trigger and the popover. */
  usage: string;
  monthlyBudget: string;
  aiUsage: string;
  aiUsageSince: (periodLabel: string) => string;
  cost: string;
  requests: string;
  tokensIn: string;
  tokensOut: string;
  viewAllUsage: string;
}

export const DEFAULT_USAGE_INDICATOR_LABELS: UsageIndicatorLabels = {
  usage: "Usage",
  monthlyBudget: "Monthly Budget",
  aiUsage: "AI Usage",
  aiUsageSince: (periodLabel) => `Your AI usage and estimated costs since ${periodLabel}.`,
  cost: "Cost",
  requests: "Requests",
  tokensIn: "Tokens in",
  tokensOut: "Tokens out",
  viewAllUsage: "View usage in settings",
};

// IconButton clones its `icon` with a `size` prop (16 at the "md" size used here) so this ring
// matches the footprint of every other sidebar icon instead of floating at its own fixed size.
const RING_STROKE = 2;

function UsageRing({ percent, size = 16 }: Readonly<{ percent: number; size?: number }>) {
  const radius = (size - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(Math.max(percent, 0), 100);
  const offset = circumference * (1 - clamped / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={RING_STROKE}
        className="stroke-border-default"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="stroke-brand-primary transition-[stroke-dashoffset]"
      />
    </svg>
  );
}

function StatCard({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="rounded-lg bg-surface-base p-2.5">
      <span className="text-[10px] font-medium text-content-tertiary uppercase tracking-wide">{label}</span>
      <div className="mt-0.5 text-sm font-semibold text-content-secondary">{value}</div>
    </div>
  );
}

export function UsageIndicator({
  budget,
  stats,
  onViewAllUsage,
  viewAllUsageLabel,
  labels,
}: Readonly<UsageIndicatorProps>) {
  const l = {
    ...DEFAULT_USAGE_INDICATOR_LABELS,
    ...(viewAllUsageLabel ? { viewAllUsage: viewAllUsageLabel } : {}),
    ...labels,
  };
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef,
    placement: "top",
    align: "start",
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !panelRef.current?.contains(target)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, panelRef]);

  const pct = Math.min(budget.percentUsed, 100);

  return (
    <div ref={anchorRef} className="relative inline-block">
      <Tooltip
        content={
          <span className="flex flex-col items-center gap-1">
            <span>
              {budget.spent} / {budget.limit}
            </span>
            <span className="text-[11px] text-content-inverse/70">{budget.resetsLabel}</span>
          </span>
        }
        disabled={isOpen}
      >
        <IconButton
          icon={<UsageRing percent={pct} />}
          aria-label={l.usage}
          variant="ghost"
          size="md"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        />
      </Tooltip>
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={l.usage}
            style={style}
            className="z-[110] w-80 rounded-xl border border-border-input bg-surface-elevated p-3 shadow-lg outline-none"
          >
            <div className="flex items-center justify-between">
              <span className="[font:var(--type-body-sm)] font-medium text-content-primary">{l.usage}</span>
              <Tooltip content={l.viewAllUsage}>
                <button
                  type="button"
                  aria-label={l.viewAllUsage}
                  onClick={() => {
                    setIsOpen(false);
                    onViewAllUsage?.();
                  }}
                  className="grid size-6 cursor-pointer place-items-center rounded-md text-content-tertiary transition-colors hover:bg-surface-base hover:text-content-primary btn-focus-ring"
                >
                  <Gauge size={14} />
                </button>
              </Tooltip>
            </div>

            <div className="mt-3">
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-medium text-content-primary">{l.monthlyBudget}</span>
                <span className="text-content-secondary">
                  {budget.spent} / {budget.limit}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-base">
                <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-content-tertiary">{budget.resetsLabel}</div>
            </div>

            <div className="mt-4 border-t border-border-subtle pt-3">
              <div className="[font:var(--type-body-sm)] font-medium text-content-primary">{l.aiUsage}</div>
              <p className="mt-0.5 text-xs text-content-tertiary">{l.aiUsageSince(stats.periodLabel)}</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <StatCard label={l.cost} value={stats.cost} />
                <StatCard label={l.requests} value={stats.requests} />
                <StatCard label={l.tokensIn} value={stats.tokensIn} />
                <StatCard label={l.tokensOut} value={stats.tokensOut} />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
